import { Button, ButtonMode, ButtonSize, Checkbox, Loader, Tooltip, useGlobalModalContext } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { difference, intersection } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { USER_INTERESTS_CACHE_KEY } from '../../../Constants';
import useFetchMarkets from '../../../hooks/markets/useFetchMarkets';
import useFetchInterests from '../../../hooks/user/useFetchInterests';
import useSaveInterests from '../../../hooks/user/useSaveInterests';
import { Market, Sector, SubSector } from '../../../models/Market';
import { NotificationMode, UserInterestSubscription } from '../../../models/User';
import { OnboardingStepProps } from '../Onboarding';
import AlertSettings from '../shared/AlertSettings';
import styles from './Interests.module.scss';
import InterestSelector from './InterestSelector';
import { MODAL_TYPES } from '../../../components/modal/GlobalModal';
import useFetchUserPermissions from '../../../hooks/user/useFetchUserPermissions';

const cx = classNames.bind(styles);
const SECTORS_COUNT_IN_TOOLTIP = 3;

const DEFAULT_ALERT_MODE = NotificationMode.Digest;

function Interests({ onBack, onContinue, show, rpOnly }: OnboardingStepProps): ReactElement | null {
  const queryClient = useQueryClient();
  const { data: markets, isLoading } = useFetchMarkets({ enabled: show });
  const { data: userInterests, isLoading: isUserInterestsLoading } = useFetchInterests({ enabled: show });
  const { mutateAsync: saveInterests } = useSaveInterests();
  const { showModal } = useGlobalModalContext();
  const [selectedMarkets, setSelectedMarkets] = useState<any>({});
  const [selectedMarketsSectors, setSelectedMarketsSectors] = useState<any>({});
  const { data: userPermissions } = useFetchUserPermissions();
  const isSingleProductUI = !userPermissions?.permissions.includes('common:product_based_alerts');

  const [alertMode, setAlertMode] = useState(DEFAULT_ALERT_MODE);

  useEffect(() => {
    if (!markets) {
      return;
    }

    const subscriptions = userInterests?.subscriptions || [];
    const marketsToSelect = {};
    const marketsSectorsToSelect = {};

    for (const market of markets) {
      const { marketSelected, selectedSectors } = getSelectionsFromSubscriptions(market, subscriptions);
      marketsSectorsToSelect[market.displayName] = selectedSectors;
      marketsToSelect[market.displayName] = marketSelected;
    }

    setSelectedMarkets(marketsToSelect);
    setSelectedMarketsSectors(marketsSectorsToSelect);

    const alertModeToSelect = getAlertModeFromSubscriptions(subscriptions);
    setAlertMode(alertModeToSelect);
  }, [userInterests, markets]);

  const isMarketSelected = (market) => {
    return selectedMarkets[market.displayName];
  };

  const isSelectionEmpty = () => {
    return markets?.every((m) => isMarketSelected(m) === false);
  };

  const getMarketAllSectorAndSubSectorTags = (market: Market) => {
    return market.sectors.flatMap((sector) => [sector.tagId, ...sector.childSectors.map((c) => c.tagId)]);
  };

  const toggleMarketSelection = (market) => {
    const marketSelected = !isMarketSelected(market);
    const marketSectors = marketSelected ? getMarketAllSectorAndSubSectorTags(market) : [];

    setSelectedMarkets({ ...selectedMarkets, [market.displayName]: marketSelected });
    setSelectedMarketsSectors({ ...selectedMarketsSectors, [market.displayName]: marketSectors });
  };

  const updateSelectedSectors = (market, tags) => {
    const marketSelected = tags.length > 0;
    setSelectedMarkets({ ...selectedMarkets, [market.displayName]: marketSelected });
    setSelectedMarketsSectors({ ...selectedMarketsSectors, [market.displayName]: tags });
  };

  const getSelectionsFromSubscriptions = (market: Market, subscriptions: UserInterestSubscription[]) => {
    const marketSubscriptions = subscriptions.filter((subscription) => difference(market.tags, subscription.tags).length === 0);
    if (marketSubscriptions.length === 0) {
      return {
        marketSelected: false,
        selectedSectors: [],
      };
    }

    const allMarketSectorAndSubSectorTags = getMarketAllSectorAndSubSectorTags(market);
    const subscribedTags = marketSubscriptions.flatMap((x) => x.tags);
    const subscribedSectorAndSubSectorTags = intersection(allMarketSectorAndSubSectorTags, subscribedTags);

    if (subscribedSectorAndSubSectorTags.length === 0) {
      return {
        marketSelected: true,
        selectedSectors: allMarketSectorAndSubSectorTags,
      };
    }

    const selectedSectors: string[] = [];
    for (const sector of market.sectors) {
      const isSectorSelected = subscribedTags.includes(sector.tagId);
      const allSubSectorsTagIds = sector.childSectors.map((x) => x.tagId);
      if (isSectorSelected) {
        selectedSectors.push(sector.tagId, ...allSubSectorsTagIds);
      } else {
        const subscribedSubSectors = intersection(allSubSectorsTagIds, subscribedTags);
        selectedSectors.push(...subscribedSubSectors);
      }
    }

    return {
      marketSelected: true,
      selectedSectors,
    };
  };

  const getAlertModeFromSubscriptions = (subscriptions: UserInterestSubscription[]) => {
    const allInstant = subscriptions.every((x) => x.mode === NotificationMode.Instant);
    return allInstant ? NotificationMode.Instant : NotificationMode.Digest;
  };

  const getOptimizedSectorSelection = (market: Market) => {
    const result: (Sector | SubSector)[] = [];

    const selectedSectorAndSubSectorTags = selectedMarketsSectors[market.displayName] || [];
    for (const sector of market.sectors) {
      const subSectorTags = sector.childSectors.map((x) => x.tagId);
      const allSubSectorsSelected = difference(subSectorTags, selectedSectorAndSubSectorTags).length === 0;
      if (allSubSectorsSelected) {
        result.push(sector);
        // eslint-disable-next-line no-continue
        continue;
      }

      for (const subSector of sector.childSectors) {
        const isSubSectorSelected = selectedSectorAndSubSectorTags.includes(subSector.tagId);
        if (isSubSectorSelected) {
          result.push(subSector);
        }
      }
    }

    return result;
  };

  const getInterestSubscriptions = (market: Market) => {
    const sectorTags = market.sectors.map((x) => x.tagId);
    const selectedSectorAndSubSectorTags = selectedMarketsSectors[market.displayName];
    const allSectorsSelected = difference(sectorTags, selectedSectorAndSubSectorTags).length === 0;

    if (allSectorsSelected) {
      return [market.tags];
    }

    const selectedSectorsOrSubSectors = getOptimizedSectorSelection(market);
    return selectedSectorsOrSubSectors.map((s) => [...market.tags, s.tagId]);
  };

  const save = () => {
    const subscriptions: UserInterestSubscription[] = [];

    for (const market of markets || []) {
      const isMarketSelected = selectedMarkets[market.displayName];
      if (!isMarketSelected) {
        // eslint-disable-next-line no-continue
        continue;
      }

      for (const tags of getInterestSubscriptions(market)) {
        subscriptions.push({
          singleProductUI: isSingleProductUI,
          mode: alertMode,
          modeResearch: alertMode,
          modeCovenants: alertMode,
          modeNews: alertMode,
          myCS: true,
          tags,
        });
      }
    }

    return saveInterests(
      { subscriptions },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([USER_INTERESTS_CACHE_KEY]);
        },
      }
    );
  };

  const handleSkip = () => {
    showModal(MODAL_TYPES.CONFIRM_SKIP_ONBOARDING);
  };

  const renderTooltip = (market: Market) => {
    const selectedSectors = getOptimizedSectorSelection(market);
    const titles = selectedSectors.slice(0, SECTORS_COUNT_IN_TOOLTIP).map((s) => s.displayName);

    if (selectedSectors.length > SECTORS_COUNT_IN_TOOLTIP) {
      titles.push(`+${selectedSectors.length - SECTORS_COUNT_IN_TOOLTIP} more`);
    }
    // eslint-disable-next-line react/no-array-index-key
    return titles.map((title, i) => <span key={`${title}-${i}`}>{title}</span>);
  };

  if (!show) {
    return null;
  }
  if (!isLoading && !isUserInterestsLoading) {
    return (
      <div className={cx('body')}>
        <div className={cx('selection-ctx')}>
          <div className={cx('selection-list-message')}>Customize the content on your feeds {!rpOnly ? 'and in email alerts' : ''} based on your selections.</div>
          <div className={cx('selections')}>
            {markets?.map((market) => (
              <div key={`role-${market.displayName}`} className={cx('selection')}>
                <div className={cx('checkbox-ctx')}>
                  <Tooltip
                    trigger={<Checkbox label={market.displayName} checked={isMarketSelected(market)} onChange={() => toggleMarketSelection(market)} dataTestId={market.displayName} />}
                    disabled={!isMarketSelected(market) || market.sectors.length === 0}
                    content={renderTooltip(market)}
                  />
                </div>
                {market.sectors.length > 0 && <InterestSelector sectors={market.sectors} selectedTags={selectedMarketsSectors[market.displayName]} onChange={(tags) => updateSelectedSectors(market, tags)} />}
              </div>
            ))}
          </div>
          {!rpOnly && (
            <div className={cx('alerts-ctx')}>
              <AlertSettings selected={alertMode} onChange={(value) => setAlertMode(value)} />
            </div>
          )}
        </div>

        <div className={cx('footer')}>
          <div className={cx('contents')}>
            <Button mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} onClick={onBack}>
              Previous
            </Button>
            <div className={cx('spacer')} />
            <Button className={cx('skip-button')} mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} onClick={handleSkip}>
              {' '}
              Skip Onboarding
            </Button>
            <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} disabled={isSelectionEmpty()} onClick={() => onContinue(save)}>
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return <Loader className="cs-loader" position="center" />;
}

export default Interests;
